import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '@/services/api';
import type { DailyEngagementFastClock } from '@/types';
import { apiErrorMessage, apiErrorStatus } from '@/utils/apiError';
import {
  FAST_CLOCK_DEFAULT_SECONDS_PER_DAY,
  FAST_CLOCK_MAX_SECONDS_PER_DAY,
  FAST_CLOCK_MIN_SECONDS_PER_DAY,
  formatDuration,
  formatIst,
  isUserId,
  isValidSecondsPerDay,
  istDaysBetween,
  realSecondsToNextIstDay,
  virtualTimeAfter,
} from './fastClock';

const { Paragraph, Text } = Typography;
const { confirm } = Modal;

/** The player id lives in the URL, so the tab survives tab switches and can be shared. */
const PLAYER_PARAM = 'fastClockUser';

/** A running clock is re-read this often, to pick up changes made elsewhere. */
const REFRESH_MS = 30_000;

interface Loaded {
  clock: DailyEngagementFastClock;
  /**
   * Real time `clock.virtualNowUtc` is taken to be for: the middle of the
   * request, so a network delay (magnified by the fast clock) is halved.
   */
  measuredAt: number;
}

interface FastClockTabProps {
  /** config:edit and the config module not read-only. */
  canEdit: boolean;
}

/**
 * Staging-only fast clock for one player's Daily Rewards: 1 IST day lasts N
 * real seconds. It is not part of the config document, so Save above does not
 * touch it; every change here applies at once.
 */
export const FastClockTab: React.FC<FastClockTabProps> = ({ canEdit }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlUser = searchParams.get(PLAYER_PARAM) ?? '';
  const [input, setInput] = useState(urlUser);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  // The look-up still running, if any (by request id), so a newer request
  // can never leave the button spinning.
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const loading = loadingId !== null;
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [speed, setSpeed] = useState<number | null>(FAST_CLOCK_DEFAULT_SECONDS_PER_DAY);
  const [now, setNow] = useState(() => Date.now());
  // Only the latest request may update the card: an earlier player's answer
  // arriving late must never replace the player now shown.
  const latestRequest = useRef(0);

  /** Starts a request; `isLatest()` turns false once a newer one starts. */
  const beginRequest = useCallback(() => {
    latestRequest.current += 1;
    const id = latestRequest.current;
    return { id, sentAt: Date.now(), isLatest: () => latestRequest.current === id };
  }, []);

  const show = useCallback((clock: DailyEngagementFastClock, sentAt: number, keepSpeed = false) => {
    const arrivedAt = Date.now();
    setLoaded({ clock, measuredAt: (sentAt + arrivedAt) / 2 });
    setNow(arrivedAt);
    setUnavailable(false);
    if (!keepSpeed) setSpeed(clock.secondsPerDay ?? FAST_CLOCK_DEFAULT_SECONDS_PER_DAY);
  }, []);

  /** 404 means this server has no fast clock (production); the rest are shown as they come. */
  const handleError = useCallback((error: unknown, fallback: string) => {
    const status = apiErrorStatus(error);
    if (status === 404) {
      setUnavailable(true);
      setLoaded(null);
    } else if (status === 409) {
      message.warning(
        'The player has a Daily Rewards action in progress (claim, spin, purchase). Try again in a moment.'
      );
    } else if (status !== 403 || apiErrorMessage(error, '').includes('read-only')) {
      // A 403 for permissions already shows the access notification.
      message.error(apiErrorMessage(error, fallback));
    }
  }, []);

  /**
   * Reads the player's clock. `silent` (the periodic re-read) keeps the card
   * and the speed being typed, and ignores failures other than a 404.
   */
  const lookUp = useCallback(
    async (userId: string, silent = false) => {
      const request = beginRequest();
      if (!silent) setLoadingId(request.id);
      try {
        const res = await adminApi.getDailyEngagementFastClock(userId);
        if (request.isLatest()) show(res.data, request.sentAt, silent);
      } catch (error) {
        if (!request.isLatest()) return;
        if (!silent) {
          setLoaded(null);
          handleError(error, "Failed to load the player's fast clock");
        } else if (apiErrorStatus(error) === 404) {
          handleError(error, '');
        }
      } finally {
        if (!silent) setLoadingId((current) => (current === request.id ? null : current));
      }
    },
    [beginRequest, handleError, show]
  );

  // Load the player in the URL on open, and again whenever it changes.
  useEffect(() => {
    setInput(urlUser);
    if (isUserId(urlUser)) lookUp(urlUser.trim());
    else {
      beginRequest(); // drops any answer still on its way
      setLoaded(null);
    }
  }, [urlUser, lookUp, beginRequest]);

  // Tick the displayed times while a player is shown.
  const clock = loaded?.clock;
  const running = clock?.secondsPerDay != null;
  useEffect(() => {
    if (!clock) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [clock]);

  // Re-read a running clock now and then: another admin may change or stop
  // it. Paused while this tab is loading or saving, so it never overtakes them.
  const shownUser = clock?.userId;
  const busy = loading || saving;
  useEffect(() => {
    if (!running || !shownUser || busy) return undefined;
    const id = window.setInterval(() => lookUp(shownUser, true), REFRESH_MS);
    return () => window.clearInterval(id);
  }, [running, shownUser, busy, lookUp]);

  const submitLookUp = () => {
    const userId = input.trim();
    if (!isUserId(userId)) {
      message.error('Enter the player’s user id (a UUID, as shown on the Players page)');
      return;
    }
    if (userId === urlUser) {
      lookUp(userId);
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(PLAYER_PARAM, userId);
        return next;
      },
      { replace: true }
    );
  };

  const set = async (
    userId: string,
    secondsPerDay: number | null,
    success: (clock: DailyEngagementFastClock) => string
  ) => {
    const request = beginRequest();
    setSaving(true);
    try {
      const res = await adminApi.setDailyEngagementFastClock(userId, secondsPerDay);
      if (request.isLatest()) show(res.data, request.sentAt);
      message.success(success(res.data));
    } catch (error) {
      // A failed change is always reported, even after another look-up.
      handleError(error, "Failed to change the player's fast clock");
    } finally {
      setSaving(false);
    }
  };

  const applySpeed = () => {
    if (!clock || !isValidSecondsPerDay(speed)) return;
    set(clock.userId, speed, () =>
      running ? `Fast clock now runs 1 day = ${speed} s` : `Fast clock on: 1 day = ${speed} s`
    );
  };

  const switchOff = () => {
    if (!clock) return;
    const { userId } = clock;
    confirm({
      title: 'Switch the fast clock off and reset this player’s Daily Rewards?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Switch off and reset',
      okButtonProps: { danger: true },
      content: (
        <>
          <Paragraph>
            Player: <Text code>{userId}</Text>
          </Paragraph>
          <Paragraph>
            The player goes back to real days as a fresh Daily Rewards account: their state, claims,
            spins, actions and notices are deleted. They start again at Day 1 with no streak and no
            loyalty tier, and get the free freezes (Freezes tab) again.
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Coins, Match Tokens and cards already granted stay. Only this player is affected.
          </Paragraph>
        </>
      ),
      onOk: () =>
        set(userId, null, (res) =>
          res.reset
            ? 'Fast clock off; the player’s Daily Rewards were reset'
            : 'The fast clock was already off; nothing was reset'
        ),
    });
  };

  const virtualMs = loaded
    ? virtualTimeAfter(
        Date.parse(loaded.clock.virtualNowUtc),
        loaded.clock.secondsPerDay,
        Math.max(0, now - loaded.measuredAt)
      )
    : 0;
  const daysAhead = loaded ? istDaysBetween(now, virtualMs) : 0;
  const speedValid = isValidSecondsPerDay(speed);
  // The controls act on the player shown; a different id typed in the box
  // must be looked up first, so nobody resets the wrong player.
  const inputIsShown = clock !== undefined && input.trim() === clock.userId;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Staging only: test one player's 28-day cycle in minutes"
        description={
          <>
            With the fast clock on, one IST day lasts the chosen number of real seconds for this
            player only; every other player keeps real days. All Daily Rewards rules (claims, missed
            days, freezes, spin guarantee, loyalty) run on the player&apos;s virtual time. At 10 s a
            day, a 20 s pause is a missed day. Changes apply at once; Save above is not needed.
          </>
        }
      />

      <Card size="small" title="Player">
        <Space.Compact style={{ width: '100%', maxWidth: 560 }}>
          <Input
            placeholder="Player user id (UUID)"
            value={input}
            allowClear
            onChange={(e) => setInput(e.target.value)}
            onPressEnter={submitLookUp}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={submitLookUp} loading={loading}>
            Look up
          </Button>
        </Space.Compact>
      </Card>

      {unavailable && (
        <Alert
          type="warning"
          showIcon
          message="The fast clock is not available on this server"
          description="The server answered 404: it exists only on staging (and test or development servers that switch it on)."
        />
      )}

      {clock && (
        <Card
          size="small"
          title={
            <Space>
              Fast clock
              {clock.secondsPerDay != null ? (
                <Tag color="orange">ON: 1 day = {clock.secondsPerDay} s</Tag>
              ) : (
                <Tag>OFF: real days</Tag>
              )}
            </Space>
          }
          extra={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => lookUp(clock.userId)}
              loading={loading}
              disabled={saving}
            >
              Refresh
            </Button>
          }
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Player">
                <Text copyable>{clock.userId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Player's time now">
                <Text strong>{formatIst(virtualMs)}</Text>
                {daysAhead > 0 && (
                  <Text type="secondary">
                    {' '}
                    ({daysAhead} IST day{daysAhead === 1 ? '' : 's'} ahead of today)
                  </Text>
                )}
                {running && (
                  <div>
                    <Text type="secondary">
                      Counted on from the server&apos;s reading (re-read every {REFRESH_MS / 1000}{' '}
                      s); the game&apos;s own day is the exact one.
                    </Text>
                  </div>
                )}
              </Descriptions.Item>
              {clock.secondsPerDay != null && (
                <Descriptions.Item label="Next IST day in">
                  {formatDuration(realSecondsToNextIstDay(virtualMs, clock.secondsPerDay))} (real
                  time)
                </Descriptions.Item>
              )}
              {clock.anchorUtc && (
                <Descriptions.Item label="Current speed since">
                  {formatIst(Date.parse(clock.anchorUtc))} (real time)
                </Descriptions.Item>
              )}
            </Descriptions>

            {!canEdit ? (
              <Text type="secondary">
                View only: changing the fast clock needs the config:edit permission.
              </Text>
            ) : !inputIsShown ? (
              <Text type="warning">
                The id in the box is not the player shown. Press Look up to load it before changing
                anything.
              </Text>
            ) : (
              <Space wrap align="start">
                <Space direction="vertical" size={2}>
                  <Space.Compact>
                    <InputNumber
                      value={speed}
                      min={FAST_CLOCK_MIN_SECONDS_PER_DAY}
                      max={FAST_CLOCK_MAX_SECONDS_PER_DAY}
                      precision={0}
                      status={speedValid ? undefined : 'error'}
                      addonAfter="s per day"
                      style={{ width: 200 }}
                      onChange={(v) => setSpeed(typeof v === 'number' ? v : null)}
                    />
                    <Button
                      type="primary"
                      onClick={applySpeed}
                      loading={saving}
                      disabled={
                        !speedValid ||
                        saving ||
                        loading ||
                        (running && speed === clock.secondsPerDay)
                      }
                    >
                      {running ? 'Change speed' : 'Switch on'}
                    </Button>
                  </Space.Compact>
                  <Text type="secondary">
                    Whole seconds, {FAST_CLOCK_MIN_SECONDS_PER_DAY} to{' '}
                    {FAST_CLOCK_MAX_SECONDS_PER_DAY.toLocaleString()}.
                    {running && ' Changing speed never moves the player’s days back.'}
                  </Text>
                </Space>
                {running && (
                  <Button danger onClick={switchOff} disabled={saving || loading}>
                    Switch off and reset
                  </Button>
                )}
              </Space>
            )}
          </Space>
        </Card>
      )}
    </Space>
  );
};
